/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react'
import { ComboBox } from '@progress/kendo-react-dropdowns'
import { Tooltip } from '@progress/kendo-react-tooltip'

import { HighlightText } from '@/matteroomTab/HighlightText'
import type { NarrativeOcgSeverity } from '@features/narrativeOcg/types'
import { cn } from '@utils/mergeClassNames'
import CLOSE_ICON from '@public/assets/action/close_circle.svg'
import WARNING_ICON from '@public/assets/action/warning-circle.svg'
import SEARCH_ICON from '@public/assets/action/icon_zoom.svg'

const TRUNCATION_TOLERANCE_PX = 2

const getCodeFieldRootClasses = status =>
  cn(
    'code-field group min-w-0 max-w-[360px] flex-[0_1_auto] text-sm',
    'default',
    status
  )

const getCodeFieldDisplayClasses = status =>
  cn(
    'relative flex min-h-10 w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-md px-1 text-sm transition-colors',
    (status === 'hover' || status === 'filled') && 'bg-[#F5F5F5]',
    status === 'focus' && 'bg-[#D9D9D9]',
    (status === 'ocg-error' || status === 'ocg-warning') && 'bg-[#F5F5F5]'
  )

const getCodeFieldBorderClasses = status =>
  cn(
    'h-0.5 shrink-0 self-stretch rounded-sm -translate-y-0.5',
    status === 'error' || status === 'ocg-error'
      ? 'bg-[#B3261E]'
      : status === 'ocg-warning'
        ? 'bg-[#FFA007]'
        : status === 'filled'
          ? 'bg-white group-hover:bg-[#D9D9D9]'
          : 'bg-[#D9D9D9]'
  )

const EditableCodeField = ({
  className = '',
  required = false,
  label,
  value,
  filterOptions,
  onFilterChange,
  onChange,
  error,
  ocgSeverity = undefined as NarrativeOcgSeverity | undefined,
  showEmptyLabel = true,
}) => {
  const [status, setStatus] = useState('default')
  const [search, setSearch] = useState('')
  const [isValueTruncated, setIsValueTruncated] = useState(false)
  const anchorRef = useRef(null)
  const comboRef = useRef(null)
  const valueRef = useRef(null)

  useEffect(() => {
    if (status === 'focus') return
    if (error) setStatus('error')
    else if (ocgSeverity === 'error') setStatus('ocg-error')
    else if (ocgSeverity === 'warning') setStatus('ocg-warning')
    else if (value) setStatus('filled')
    else setStatus('default')
  }, [value, error, ocgSeverity])

  useEffect(() => {
    const handleClickOutside = event => {
      setTimeout(() => {
        const dropdown = document.querySelector('.k-animation-container')
        const comboEl = comboRef.current?.element

        if (event.target.closest('.k-i-x')) return

        const clickInsideCombo = comboEl?.contains(event.target)
        const clickInsideDropdown = dropdown?.contains(event.target)

        if (status === 'focus' && !clickInsideCombo && !clickInsideDropdown) {
          if (error) setStatus('error')
          else if (ocgSeverity === 'error') setStatus('ocg-error')
          else if (ocgSeverity === 'warning') setStatus('ocg-warning')
          else setStatus(value ? 'filled' : 'default')
        }
      }, 0)
    }

    document.addEventListener('mouseup', handleClickOutside)
    return () => document.removeEventListener('mouseup', handleClickOutside)
  }, [status, value, error, ocgSeverity])

  useEffect(() => {
    if (status === 'focus') {
      setSearch('')
      setTimeout(() => {
        if (comboRef.current) {
          comboRef.current.focus()
          comboRef.current.toggleBtnClick()
        }
      }, 0)
    }
  }, [status])

  useEffect(() => {
    const updateTruncation = () => {
      const element = valueRef.current
      setIsValueTruncated(
        Boolean(
          element &&
          element.scrollWidth - element.clientWidth > TRUNCATION_TOLERANCE_PX
        )
      )
    }

    updateTruncation()
    window.addEventListener('resize', updateTruncation)
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateTruncation)

    if (valueRef.current) resizeObserver?.observe(valueRef.current)

    return () => {
      window.removeEventListener('resize', updateTruncation)
      resizeObserver?.disconnect()
    }
  }, [status, value])

  return (
    <div
      className={cn(getCodeFieldRootClasses(status), className)}
      onMouseEnter={() => status === 'default' && setStatus('hover')}
      onMouseLeave={() => status === 'hover' && setStatus('default')}
    >
      {status === 'focus' ? (
        <ComboBox
          ref={comboRef}
          key={status === 'focus' ? 'focus' : 'display'}
          className={`search-combobox relative flex w-full items-center text-sm [&.k-combobox.k-focus]:!shadow-none [&.k-combobox:focus-within]:!shadow-none [&_.k-input-inner]:!border-0 [&_.k-input-inner]:!bg-transparent [&_.k-input-inner]:!text-sm [&_.k-input-inner]:!shadow-none ${value ? 'is-filled' : ''}`}
          placeholder={label}
          data={filterOptions}
          textField="name"
          dataItemKey="code"
          suggest
          filterable
          value={value}
          popupSettings={{
            className:
              'search-combobox-popup custom-combobox-popup mt-1 box-border max-h-[220px] overflow-hidden rounded bg-white shadow-[0_4px_4px_rgba(0,0,0,0.10)] [&_.k-list-content]:!min-w-0 [&_.k-list-content]:!max-w-full [&_.k-list-content]:!overflow-x-hidden [&_.k-list-content]:!w-full [&_.k-list-item-text]:!block [&_.k-list-item-text]:!min-w-0 [&_.k-list-item-text]:!max-w-full [&_.k-list-item-text]:!truncate [&_.k-list-item-text]:!w-full [&_.k-list-item]:!box-border [&_.k-list-item]:!min-w-0 [&_.k-list-item]:!max-w-full [&_.k-list-item]:!overflow-hidden [&_.k-list-item]:!w-full [&_.k-list-ul]:!min-w-0 [&_.k-list-ul]:!max-w-full [&_.k-list-ul]:!w-full [&_.k-list]:!min-w-0 [&_.k-list]:!max-w-full [&_.k-list]:!overflow-x-hidden [&_.k-list]:!w-full [&_.k-popup]:!min-w-0 [&_.k-popup]:!max-w-full [&_.k-popup]:!w-full [&_.k-virtual-content]:!min-w-0 [&_.k-virtual-content]:!max-w-full [&_.k-virtual-content]:!overflow-x-hidden [&_.k-virtual-content]:!w-full [&_.k-list-optionlabel]:!box-border [&_.k-list-optionlabel]:!min-w-0 [&_.k-list-optionlabel]:!max-w-full [&_.k-list-optionlabel]:!overflow-hidden [&_.k-list-optionlabel]:!w-full',
          }}
          onFilterChange={event => {
            setSearch(event.filter?.value ?? '')
            onFilterChange(event)
          }}
          onChange={event => {
            onChange(event.value)
            if (!event.value) return
            setTimeout(() => setStatus(event.value ? 'filled' : 'default'), 100)
          }}
          itemRender={(li, itemProps) => (
            <HighlightText
              name={itemProps.dataItem?.name ?? ''}
              highlight={search}
              li={li}
              enableTooltip
            />
          )}
        />
      ) : status === 'error' ? (
        <>
          <div
            className="box-border flex h-10 cursor-pointer items-center gap-2 rounded border border-[#B3261E] px-1"
            onClick={() => setStatus('focus')}
          >
            <span className="min-w-[60px] text-sm font-normal text-[#999]">
              {required && (
                <span className="text-sm font-normal text-[#F55A4E]">*</span>
              )}
              {label}
            </span>
            <img className="h-4 w-4" src={WARNING_ICON} alt="warning" />
            <img className="search" src={SEARCH_ICON} alt="" />
          </div>
          {error && <div className="error-text">{error}</div>}
        </>
      ) : (
        <>
          <div
            ref={anchorRef}
            className={getCodeFieldDisplayClasses(status)}
            onClick={() => setStatus('focus')}
          >
            {!value && showEmptyLabel && (
              <span className="min-w-[60px] text-sm font-normal text-[#999]">
                {(required || ocgSeverity === 'error') && (
                  <span className="text-sm font-normal text-[#F55A4E]">*</span>
                )}
                {label}
              </span>
            )}

            {value && (
              <div className="min-w-0 flex-1 overflow-hidden">
                <Tooltip
                  tooltipStyle={{
                    maxWidth: '264px',
                    border: 'none',
                    backgroundColor: '#000',
                    color: '#fff',
                    padding: '4px 8px',
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                  anchorElement="target"
                  position="top"
                >
                  <span
                    ref={valueRef}
                    title={isValueTruncated ? value?.name : undefined}
                    className="block w-full min-w-0 cursor-default truncate text-sm font-normal text-[#333]"
                  >
                    {value.name}
                  </span>
                </Tooltip>
              </div>
            )}

            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {value && (
                <img
                  className="cursor-pointer"
                  src={CLOSE_ICON}
                  onClick={event => {
                    event.stopPropagation()
                    onChange(null)
                  }}
                  alt="clear"
                />
              )}
              <img className="search" src={SEARCH_ICON} alt="" />
            </div>
          </div>

          <div className={getCodeFieldBorderClasses(status)} />
        </>
      )}

      {error && status !== 'error' && <div className="error-text">{error}</div>}
    </div>
  )
}

export default EditableCodeField
